import { HourglassOutlined } from "@ant-design/icons";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { Button, Card, Typography } from "antd";
import queryClient from "api/queryClient.ts";
import { composeGetOfferQueryOptions } from "api/TenantSlug/Offer/queryOptions.ts";
import { useStoreRequestMutation } from "api/TenantSlug/Request/hooks.ts";
import AccessDenied from "assets/access_denied.svg";
import AccessPending from "assets/access_pending.svg";
import AccessPrivate from "assets/access_private.svg";
import { justRequestedOfferAccessAtom } from "atoms/justRequestedOfferAccessAtom.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions.ts";
import { useUser } from "hooks/useUser.ts";
import { useRecoilState } from "recoil";
import { PermissionEnum } from "types/enums.ts";
import { getOfferAccessGateState } from "utils/getOfferAccessGateState.ts";

export const Route = createFileRoute("/_authenticated/$tenantSlug/_layout/offer/_layout/detail/$offerId/_layout")({
    component: OfferDetailLayout,
    loader: async ({ params: { offerId } }) => {
        let breadcrumb = offerId;

        try {
            const { offer } = await queryClient.ensureQueryData(
                composeGetOfferQueryOptions(offerId, {
                    filter: { include: "affiliates,userRequests" },
                })
            );

            breadcrumb = offer.visible_title;
        } catch (e) {
            console.error(e);
        }

        return {
            breadcrumb,
        };
    },
    pendingComponent: () => <Pending />,
});

function OfferDetailLayout() {
    const { t } = usePaldockTranslation("pages.offer.detail");
    const { offerId } = Route.useParams();
    const permissionUtils = usePermission();
    const {
        data,
        refetch,
        isPending: isRequestsPending,
    } = useSuspenseQuery(
        composeGetOfferQueryOptions(offerId, {
            filter: { include: "affiliates,userRequests" },
        })
    );

    const offer = data.offer;

    const userUtils = useUser();
    const activeUser = userUtils.getAuthenticatedUser();

    const { mutateAsync: createRequest, isPending: isCreateRequestPending } = useStoreRequestMutation();

    const [justRequestedOfferAccess, setJustRequestedOfferAccess] = useRecoilState(justRequestedOfferAccessAtom);
    const justRequestedThisOffer = justRequestedOfferAccess.includes(offerId);

    const handleRequestAccess = async () => {
        if (!activeUser || !offer) return;

        setJustRequestedOfferAccess((current) => (current.includes(offerId) ? current : [...current, offerId]));

        await createRequest({
            user_id: activeUser.id,
            type: "offer",
            offer_id: offer.id,
        });

        await refetch();
    };

    if (!offer || !offer.id) {
        return <Pending />;
    }

    const gateState = getOfferAccessGateState({
        access: offer.access,
        affiliates: offer.affiliates,
        userRequests: offer.user_requests,
        currentUserId: userUtils.getAuthenticatedUser()?.id,
        hasUnrestrictedPermission: permissionUtils.hasPermission(PermissionEnum.OfferViewAccessUnrestricted),
    });

    switch (gateState) {
        case "restricted-pending":
            return (
                <Card className="rounded-md">
                    <div className={"flex items-center justify-center flex-col"}>
                        <img src={AccessPending} alt={t("offer-access.pending.alt")} className={"my-[24px]"} />
                        <Typography.Title level={4} className={"mb-0"}>
                            {justRequestedThisOffer
                                ? t("offer-access.pending.justRequestedTitle")
                                : t("offer-access.pending.title")}
                        </Typography.Title>
                        <p className={"mb-[30px] text-black/45"}>
                            {justRequestedThisOffer
                                ? t("offer-access.pending.justRequestedMessage")
                                : t("offer-access.pending.message")}
                        </p>
                        <Button type="primary" icon={<HourglassOutlined />} disabled={true}>
                            {t("offer-access.pending.button")}
                        </Button>
                    </div>
                </Card>
            );
        case "restricted-locked":
            return (
                <Card className="rounded-md">
                    <div className={"flex items-center justify-center flex-col"}>
                        <img src={AccessDenied} alt={t("offer-access.locked.alt")} className={"my-[24px]"} />
                        <Typography.Title level={4} className={"mb-0"}>
                            {t("offer-access.locked.title")}
                        </Typography.Title>
                        <p className={"mb-[30px] text-black/45"}>{t("offer-access.locked.message")}</p>
                        <Button
                            type="primary"
                            onClick={handleRequestAccess}
                            disabled={isCreateRequestPending || isRequestsPending}
                        >
                            {t("offer-access.locked.button")}
                        </Button>
                    </div>
                </Card>
            );
        case "private-blocked":
            return (
                <Card className="rounded-md">
                    <div className={"flex items-center justify-center flex-col"}>
                        <img src={AccessPrivate} alt={t("offer-access.private.alt")} className={"my-[24px]"} />
                        <Typography.Title level={4} className={"mb-0"}>
                            {t("offer-access.private.title")}
                        </Typography.Title>
                        <p className={"mb-[30px] text-black/45"}>{t("offer-access.private.message")}</p>
                    </div>
                </Card>
            );
        case "granted":
            return <Outlet />;
    }
}

function Pending() {
    const { t: tC } = usePaldockTranslation("common");
    return <div>{tC("loading")}</div>;
}
